import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { useState, useRef, useEffect, useMemo } from 'react'
import { useWindowSize, useLocalStorage } from 'usehooks-ts'
import styled from 'styled-components'
import { TextWidth, TextWidthExtended } from '@ta-interaktiv/react-disco-grid'
import { de, fr } from 'Locale/texts'
import Header from '../Header'
import Slider from '../Slider'
import RadioButtons from 'components/RadioButtons'
import BarChart from 'components/BarChart'
import LineChartCanton from 'components/LineChartCanton'
import LineChartCountry from 'components/LineChartCountry'
import { TableCountry } from 'components/TableCountry'
import { AnnotatedImage } from 'components/AnnotatedImage'
import CircleAnnotation from 'components/CircleAnnotation'
import { GlobalVariables } from 'Globals/GlobalVariables'
import DynamicText from 'components/DynamicText'
import debounce from 'lodash/debounce'

import popuplation_m from '../../data/population_m.json'
import popuplation_f from '../../data/population_f.json'
import m_1972 from '../../data/men_1972.json'
import m_1878 from '../../data/men_1878.json'
import history_m from '../../data/history.json'
import history_f from '../../data/history.json'
import country_history_m from '../../data/country_history.json'
import country_history_f from '../../data/country_history.json'
import w_1940 from '../../data/women_1940.json'

import lightImageMap from '../../images/heights_map.png'
import darkImageMap from '../../images/heights_map_dark.png'
import { CaptionChart } from 'components/CaptionChart'

const debug = window.document.title === 'TA-Interaktiv Microfrontend'

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const [bodyHeight, setBodyHeight] = useState(165)
  const [gender, setGender] = useLocalStorage('gender2024Koerpergroessen', 0)
  // const [gender, setGender] = useState(0)
  // const [update, setUpdate] = useState(false)
  const [chartsToDisplay, setChartstoDisplay] = useState(1)
  const [sliderRange, setSliderRange] = useState([130, 200])
  const [populationComparison, setPopulationComparison] = useState([50, 50])
  const [historyComparison, setHistoryComparison] = useState([50, 50])

  const todayRef = useRef<ForwardRefObject>(null)
  const century20Ref = useRef<ForwardRefObject>(null)
  const century19Ref = useRef<ForwardRefObject>(null)

  const [height, setHeight] = useState(0)
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const chartEndRef = useRef<HTMLDivElement>(null)

  const locale = tenant.lang === 'fr' ? fr : de

  useEffect(() => {
    var smaller_population = 0
    var larger_population = 0
    var smaller_history = 0
    var larger_history = 0
    var i

    if (gender === 0) {
      for (i = 0; i < popuplation_f.length; i++) {
        if (popuplation_f[i].Groesse < bodyHeight) {
          smaller_population += popuplation_f[i].share
        } else if (popuplation_f[i].Groesse > bodyHeight) {
          larger_population += popuplation_f[i].share
        }
      }
      for (i = 0; i < w_1940.length; i++) {
        if (w_1940[i].Groesse < bodyHeight) {
          smaller_history += w_1940[i].share
        } else if (w_1940[i].Groesse > bodyHeight) {
          larger_history += w_1940[i].share
        }
      }
    } else {
      for (i = 0; i < popuplation_m.length; i++) {
        if (popuplation_m[i].Groesse < bodyHeight) {
          smaller_population += popuplation_m[i].share
        } else if (popuplation_m[i].Groesse > bodyHeight) {
          larger_population += popuplation_m[i].share
        }
      }
      for (i = 0; i < m_1878.length; i++) {
        if (m_1878[i].Groesse < bodyHeight) {
          smaller_history += m_1878[i].share
        } else if (m_1878[i].Groesse > bodyHeight) {
          larger_history += m_1878[i].share
        }
      }
    }
    setPopulationComparison([smaller_population, larger_population])
    setHistoryComparison([smaller_history, larger_history])
  }, [bodyHeight, gender])

  useEffect(() => {
    setHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    gender === 1 ? setSliderRange([140, 210]) : setSliderRange([130, 200])
    gender === 0 ? setChartstoDisplay(1) : setChartstoDisplay(2)
  }, [gender])

  useEffect(() => {
    gender === 1 ? setSliderRange([140, 210]) : setSliderRange([130, 200])
    gender === 0 ? setChartstoDisplay(1) : setChartstoDisplay(2)
  }, [gender])

  const historyArray = useMemo(() => {
    const result = Object.entries(gender === 0 ? history_f : history_m).map(
      ([key, value]) => ({ year: Number(key), ...value })
    )
    return result
  }, [])
  // const countryHistoryArray = Object.entries(
  //   gender === 0 ? country_history_f : country_history_m
  // ).map(([key, value]) => ({ year: Number(key), ...value }))

  const countryHistoryArray = useMemo(() => {
    const result = Object.entries(
      gender === 0 ? country_history_f : country_history_m
    ).map(([key, value]) => ({ year: Number(key), ...value }))
    return result
  }, [])

  const handleEvaluate = (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    todayRef.current?.drawChart()
    century20Ref.current?.drawChart()
    century19Ref.current?.drawChart()
  }

  const debouncedOnChange = debounce(handleEvaluate, 500)

  useEffect(() => {
    debouncedOnChange()
  }, [bodyHeight, gender])

  var part = properties?.part || 0

  return (
    <AppContainer $colorMode={colorMode}>
      {(part === 'header' || debug) && (
        <Header
          tenant={tenant}
          colorMode={colorMode}
          title={locale.header.title}
          lead={locale.header.lead}
          pubDate={locale.header.pubDate}
        />
      )}
      {debug && (
        <TextWidth>
          Lorem ipsum dolor sit amet consectetur adipisicing elit. Impedit, id
          suscipit blanditiis consequuntur aperiam, ipsum tenetur molestiae
          maxime dignissimos vel ducimus libero inventore repudiandae. Molestiae
          excepturi unde exercitationem sapiente labore?
        </TextWidth>
      )}
      {(part === 'calculator' || debug) && (
        <>
          <TextWidth>
            <UserInput>
              <Title>
                {locale.calculator.title}
                <div className='subtitle'>{locale.calculator.subtitle}</div>
              </Title>
              <InputElement>
                <div>{locale.calculator.inputHeight}</div>
                <Slider
                  range={sliderRange}
                  bodyHeight={bodyHeight}
                  setBodyHeight={setBodyHeight}
                />
              </InputElement>
              <Spacer />
              <InputElement>
                {locale.calculator.inputGender}
                <RadioButtons
                  current={gender}
                  options={[
                    { label: locale.man, value: 1 },
                    { label: locale.woman, value: 0 },
                  ]}
                  updateGender={setGender}
                />
              </InputElement>
              <ButtonWrapper>
                <Button
                  onClick={(e) => {
                    if (chartEndRef.current) {
                      chartEndRef.current.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                      })
                      handleEvaluate(e)
                    }
                  }}
                >
                  {locale.calculator.button}
                </Button>
              </ButtonWrapper>
            </UserInput>
          </TextWidth>
          <TextWidthExtended>
            <div
              ref={chartEndRef}
              style={{
                top: isMobile ? height * 0.1 + 'px' : height * 0.2 + 'px',
              }}
            />
            <BarChartContainer $isMobile={isMobile}>
              <Title>
                {locale.barchart.title}{' '}
                {gender === 0 ? locale.women_gen : locale.men_gen}
              </Title>
              <div className='big'>
                <BarChart
                  data={gender === 0 ? popuplation_f : popuplation_m}
                  bodyHeight={bodyHeight}
                  gender={gender}
                  youLabel={locale.barchart.you}
                  yLabelM={locale.barchart.yLabelm}
                  yLabelW={locale.barchart.yLabelw}
                  // mean={gender === 0 ? 166 : 178}
                  time={locale.barchart.today}
                  ref={todayRef}
                  isMobile={isMobile}
                />
              </div>
              <CaptionChart
                source={locale.sources.healthSurvey}
                link={
                  'https://www.sciencedirect.com/science/article/pii/S221133552200287X?via%3Dihub'
                }
                colorMode={colorMode}
              ></CaptionChart>
            </BarChartContainer>

            <DynamicTextContainer
              className={colorMode === 'dark' ? 'dark' : 'light'}
            >
              {/* {populationComparison[0].toFixed(1) == '0.0' ||
              populationComparison[1].toFixed(1) == '100.0' ? (
                <>
                  Sie gehören heute zu den <b>0,01 Prozent</b> kleinsten{' '}
                  {gender === 0 ? 'Frauen' : 'Männern'} in der Schweizer
                  Gesamtpopulation - <b>99,9 Prozent</b> sind grösser als Sie.
                  Im Durchschnitt sind die erwachsenen{' '}
                  {gender === 0 ? 'Frauen' : 'Männer'}{' '}
                  <b>{gender === 0 ? '164,6 cm' : '177,4 cm'}</b> gross.
                </>
              ) : populationComparison[0].toFixed(1) == '100.0' ||
                populationComparison[1].toFixed(1) == '0.0' ? (
                <>
                  Sie gehören heute zum grössten <b>0,01 Prozent</b> der
                  Schweizer {gender === 0 ? 'Frauen' : 'Männer'} -{' '}
                  <b>99,9 Prozent</b> sind kleiner als Sie. Im Durchschnitt sind
                  die erwachsenen {gender === 0 ? 'Frauen' : 'Männer'}{' '}
                  <b>{gender === 0 ? '164,6 cm' : '177,4 cm'}</b> gross.
                </>
              ) : (
                <>
                  Heute sind{' '}
                  <b>
                    {populationComparison[0].toFixed(1).replace('.', ',')}{' '}
                    Prozent
                  </b>{' '}
                  der {gender === 0 ? 'Frauen' : 'Männer'} aus der Schweizer
                  Gesamtpopulation kleiner als Sie - und{' '}
                  <b>
                    {populationComparison[1].toFixed(1).replace('.', ',')}{' '}
                    Prozent
                  </b>{' '}
                  sind grösser. Im Durchschnitt sind die erwachsenen{' '}
                  {gender === 0 ? 'Frauen' : 'Männer'}{' '}
                  <b>{gender === 0 ? '164,6 cm' : '177,4 cm'}</b> gross.
                </>
              )} */}
              <DynamicText
                lang={tenant.lang}
                textNumber={1}
                values={populationComparison}
                gender={gender}
              />
            </DynamicTextContainer>

            {chartsToDisplay === 2 ? (
              <BarChartContainer $isMobile={isMobile}>
                <div className='small'>
                  <BarChart
                    data={m_1972}
                    bodyHeight={bodyHeight}
                    gender={gender}
                    youLabel={locale.barchart.you}
                    yLabelM={locale.barchart.yLabelm}
                    yLabelW={locale.barchart.yLabelw}
                    time='1940'
                    ref={century20Ref}
                    isMobile={isMobile}
                  />
                  <CaptionChart
                    source={locale.sources.smw}
                    link={'https://smw.ch/index.php/smw/article/view/1326/1536'}
                    colorMode={colorMode}
                  ></CaptionChart>
                </div>

                <div className='small'>
                  <BarChart
                    data={m_1878}
                    bodyHeight={bodyHeight}
                    gender={gender}
                    youLabel={locale.barchart.you}
                    yLabelM={locale.barchart.yLabelm}
                    yLabelW={locale.barchart.yLabelw}
                    time='1870'
                    ref={century19Ref}
                    isMobile={isMobile}
                  />
                  <CaptionChart
                    source={locale.sources.smw}
                    link={'https://smw.ch/index.php/smw/article/view/1326/1536'}
                    colorMode={colorMode}
                  ></CaptionChart>
                </div>
              </BarChartContainer>
            ) : (
              <></>
            )}
            {chartsToDisplay === 1 ? (
              // <TextWidth>
              <SmallChartContainer $isMobile={isMobile}>
                <BarChart
                  data={w_1940}
                  bodyHeight={bodyHeight}
                  gender={gender}
                  youLabel={locale.barchart.you}
                  yLabelM={locale.barchart.yLabelm}
                  yLabelW={locale.barchart.yLabelw}
                  time='1940'
                  ref={century20Ref}
                  isMobile={isMobile}
                />
                <CaptionChart
                  source={locale.sources.healthSurvey}
                  link={
                    'https://www.sciencedirect.com/science/article/pii/S221133552200287X?via%3Dihub'
                  }
                  colorMode={colorMode}
                ></CaptionChart>
              </SmallChartContainer>
            ) : (
              // </TextWidth>
              <></>
            )}
            <DynamicTextContainer
              className={colorMode === 'dark' ? 'dark' : 'light'}
            >
              {/* {gender === 0 ? (
                historyComparison[0].toFixed(1) == '100.0' ||
                historyComparison[1].toFixed(1) == '0.0' ? (
                  <>
                    <b>Vor 84 Jahren</b> lag die Durchschnittsgrösse von Frauen
                    bei <b>161,9 cm</b> – knapp 3 cm kleiner als heute. Sie
                    würden mit Ihrer heutigen Körpergrösse <b>1940</b> zu den{' '}
                    <b>0,01 Prozent</b> grössten Frauen gehören -{' '}
                    <b>99,9 Prozent</b> wären kleiner als Sie.
                  </>
                ) : (
                  <>
                    <b>Vor 84 Jahren</b> lag die Durchschnittsgrösse von Frauen
                    bei <b>161,9 cm</b> – knapp 3 cm kleiner als heute. Sie
                    würden mit Ihrer heutigen Körpergrösse <b>1940</b> zu den{' '}
                    <b>
                      {historyComparison[1].toFixed(1).replace('.', ',')}{' '}
                      Prozent
                    </b>{' '}
                    grössten Frauen gehören -{' '}
                    <b>
                      {historyComparison[0].toFixed(1).replace('.', ',')}{' '}
                      Prozent
                    </b>{' '}
                    wären kleiner als Sie.
                  </>
                )
              ) : historyComparison[0].toFixed(1) == '100.0' ||
                historyComparison[1].toFixed(1) == '0.0' ? (
                <>
                  <b>1940</b> lag die Durchschnittsgrösse von Männern bei{' '}
                  <b>174,4 cm</b> – <b>1870</b> gar nur bei <b>163,3 cm</b>. Mit
                  ihrer heutigen Körpergrösse hätten sie vor 150 Jahren zu den{' '}
                  <b>0,01 Prozent</b> grössten Männern gehört – und{' '}
                  <b>99,9 Prozent</b> wären kleiner als Sie gewesen.
                </>
              ) : (
                <>
                  <b>1940</b> lag die Durchschnittsgrösse von Männern bei{' '}
                  <b>174,4 cm</b> – <b>1870</b> gar nur bei <b>163,3 cm</b>. Mit
                  ihrer heutigen Körpergrösse hätten sie vor 150 Jahren zu den{' '}
                  <b>
                    {historyComparison[1].toFixed(1).replace('.', ',')} Prozent
                  </b>{' '}
                  grössten Männern gehört – und{' '}
                  <b>
                    {historyComparison[0].toFixed(1).replace('.', ',')} Prozent
                  </b>{' '}
                  wären kleiner als Sie gewesen.
                </>
              )} */}
              <DynamicText
                lang={tenant.lang}
                textNumber={2}
                values={historyComparison}
                gender={gender}
              />
            </DynamicTextContainer>
          </TextWidthExtended>
        </>
      )}

      {debug && (
        <>
          <TextWidth>
            Lorem ipsum dolor sit amet consectetur adipisicing elit. Impedit, id
            suscipit blanditiis consequuntur aperiam, ipsum tenetur molestiae
            maxime dignissimos vel ducimus libero inventore repudiandae.
            Molestiae excepturi unde exercitationem sapiente labore? Lorem ipsum
            dolor sit amet consectetur adipisicing elit. Impedit, id suscipit
            blanditiis consequuntur aperiam, ipsum tenetur molestiae maxime
            dignissimos vel ducimus libero inventore repudiandae. Molestiae
            excepturi unde exercitationem sapiente labore?
          </TextWidth>
        </>
      )}
      {(part === 'history_cantons' || debug) && (
        <TextWidth>
          <LineChartCanton
            data={historyArray}
            colorMode={colorMode}
            title={locale.linechartCanton.title}
            subtitle={locale.linechartCanton.subtitle}
            lang={tenant.lang}
          ></LineChartCanton>
          <CaptionChart
            source={locale.sources.smvAjb}
            link={
              'https://smw.ch/index.php/smw/article/view/1326/1536&sa=D&source=docs&ust=1708355850841275&usg=AOvVaw0QxJOOEz1H8D2vm2oSHX4y'
            }
            colorMode={colorMode}
          ></CaptionChart>
        </TextWidth>
      )}
      {debug && (
        <>
          <TextWidth>
            Lorem ipsum dolor sit amet consectetur adipisicing elit. Impedit, id
            suscipit blanditiis consequuntur aperiam, ipsum tenetur molestiae
            maxime dignissimos vel ducimus libero inventore repudiandae.
            Molestiae excepturi unde exercitationem sapiente labore? Lorem ipsum
            dolor sit amet consectetur adipisicing elit. Impedit, id suscipit
            blanditiis consequuntur aperiam, ipsum tenetur molestiae maxime
            dignissimos vel ducimus libero inventore repudiandae. Molestiae
            excepturi unde exercitationem sapiente labore?
          </TextWidth>
        </>
      )}
      {(part === 'history_nations' || debug) && (
        <TextWidth>
          <LineChartCountry
            data={countryHistoryArray}
            colorMode={colorMode}
            title={locale.linechartCountry.title}
            subtitle={locale.linechartCountry.subtitle}
            labelNames={locale.linechartCountry.keys}
          ></LineChartCountry>
          <CaptionChart
            source={locale.sources.ncdRisk}
            link={'https://elifesciences.org/articles/13410'}
            colorMode={colorMode}
          ></CaptionChart>
        </TextWidth>
      )}
      {debug && (
        <>
          <TextWidth>
            Lorem ipsum dolor sit amet consectetur adipisicing elit. Impedit, id
            suscipit blanditiis consequuntur aperiam, ipsum tenetur molestiae
            maxime dignissimos vel ducimus libero inventore repudiandae.
            Molestiae excepturi unde exercitationem sapiente labore? Lorem ipsum
            dolor sit amet consectetur adipisicing elit. Impedit, id suscipit
            blanditiis consequuntur aperiam, ipsum tenetur molestiae maxime
            dignissimos vel ducimus libero inventore repudiandae. Molestiae
            excepturi unde exercitationem sapiente labore?
          </TextWidth>
        </>
      )}
      {(part === 'map_switzerland' || debug) && (
        <>
          <TextWidth>
            <Title>
              {locale.map.title}{' '}
              <div className='subtitle'>{locale.map.subtitle}</div>
            </Title>
          </TextWidth>
          <TextWidthExtended>
            <AnnotatedImage
              img={colorMode === 'dark' ? darkImageMap : lightImageMap}
              isMobile={isMobile}
              annotations={[
                {
                  content: (
                    <CircleAnnotation
                      offsetx={isMobile ? 0 : 40}
                      offsety={isMobile ? -85 : 0}
                    >
                      {locale.map.annotation.AI}
                    </CircleAnnotation>
                  ),
                  position: { x: 77, y: 24 },
                },
                {
                  content: (
                    <CircleAnnotation
                      offsetx={isMobile ? -35 : -240}
                      offsety={isMobile ? -105 : -70}
                    >
                      {locale.map.annotation.ZH}
                    </CircleAnnotation>
                  ),
                  position: { x: 58, y: 24 },
                },
                {
                  content: (
                    <CircleAnnotation
                      offsetx={isMobile ? -1 : 0}
                      offsety={isMobile ? 45 : 50}
                    >
                      {locale.map.annotation.VS}
                    </CircleAnnotation>
                  ),
                  position: { x: 50, y: 73 },
                },
              ]}
              colorMode={colorMode}
              legend={locale.map.legend}
            />
            <CaptionChart
              source={locale.sources.uzh}
              link={
                'https://www.sciencedirect.com/science/article/abs/pii/S1570677X16300478'
              }
              colorMode={colorMode}
            ></CaptionChart>
          </TextWidthExtended>
        </>
      )}

      {(part === 'table_country' || debug) && (
        <>
          <TextWidth>
            <TableCountry
              gender={gender}
              title={locale.tableCountry.title}
              subtitle={locale.tableCountry.subtitle}
              men={locale.men}
              women={locale.women}
              lang={tenant.lang}
            />
          </TextWidth>
        </>
      )}
      {debug && (
        <>
          <TextWidth>
            Lorem ipsum dolor sit amet consectetur adipisicing elit. Impedit, id
            suscipit blanditiis consequuntur aperiam, ipsum tenetur molestiae
            maxime dignissimos vel ducimus libero inventore repudiandae.
            Molestiae excepturi unde exercitationem sapiente labore? Lorem ipsum
            dolor sit amet consectetur adipisicing elit. Impedit, id suscipit
            blanditiis consequuntur aperiam, ipsum tenetur molestiae maxime
            dignissimos vel ducimus libero inventore repudiandae. Molestiae
            excepturi unde exercitationem sapiente labore?
          </TextWidth>
        </>
      )}
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
`

const UserInput = styled.div`
  background-color: var(--bg-user-input);
  margin: 30px auto 90px;
  color: var(--text-color);
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  /* Responsive layout - makes a one column layout instead of a two-column layout */

  gap: 0px 20px;
  padding: 30px;
  box-shadow: var(--shadow);
  @media (max-width: 800px) {
    flex-direction: column;
  }
`

const Title = styled.div`
  flex: 0 0 100%;
  font-size: 23px;
  line-height: 1.4em;
  font-weight: 200;
  letter-spacing: 0.6px;
  text-align: left;
  margin-bottom: 30px;

  .subtitle {
    display: inline-block;
    font-size: 13px;
    line-height: 1.4em;
    width: 100%;
    color: var(--subtitle);
    padding-top: 0.75em;
  }
`
const Spacer = styled.div`
  /* border: 0.5px dotted var(--gray-4); */
  height: 40px;
  @media screen and (max-width: 712px) {
    height: 25px;
  }
  /* margin-bottom: 40px; */
`
const InputElement = styled.div`
  flex: 1;
  /* border: 0.5px dotted var(--gray-4); */
  height: 120px;
  /* margin-bottom: 40px; */
`

const ButtonWrapper = styled.div`
  width: 100%;
  margin-top: 2em;
`

const Button = styled.button`
  max-width: 200px;
  color: var(--site-background);
  background-color: var(--text-color);
  font-size: 18px;
  padding: 15px;
  letter-spacing: 0.4px;
  border: 0;
  margin-left: auto;
  margin-right: auto;
  margin-top: 30px;
  @media screen and (min-width: 712px) {
    &:hover {
      background-color: var(--yellow-main);
      color: var(--brandblue-main);
      cursor: pointer;
    }
  }
`

const BarChartContainer = styled.div<{ $isMobile?: boolean }>`
  display: grid;
  grid-template-columns: auto auto;
  gap: 10px;
  .big {
    grid-column-start: 1;
    grid-column-end: 3;
    height: ${(props) => (props.$isMobile ? '350px' : '420px')};
  }
  .small {
    height: ${(props) => (props.$isMobile ? '250px' : '350px')};
    ${({ $isMobile }) =>
      $isMobile &&
      ` grid-column-start: 1;
    grid-column-end: 3;
  `}
    margin-bottom: 60px;
  }
`
const SmallChartContainer = styled.div<{ $isMobile?: boolean }>`
  height: ${(props) => (props.$isMobile ? '300px' : '400px')};
  /* max-width: 668px; */
  margin: 0 auto;
  margin-bottom: 60px;
`

const DynamicTextContainer = styled.div`
  margin-bottom: 60px;
  background-color: var(--gray-5);
  color: var(--text-color);

  padding: 20px;
  border-radius: 10px;
  font-size: 18px;
  line-height: 1.6em;
  @media screen and (max-width: 711px) {
    font-size: 15px;
  }
  &.dark {
    background-color: #242424;
  }
`

export default MainComponent
